<script setup lang="ts">
import { computed, useAttrs, defineEmits } from 'vue'

const props = withDefaults(
  defineProps<{
    autoGrow?: boolean
    disabled?: boolean
    label?: string
    items?: string[]
    height?: number
    selectedItem: string | null
  }>(),
  {
    autoGrow: false,
    disabled: false,
    items: () => [],
    height: 100,
    selectedItem: '',
  },
)

const emit = defineEmits<{
  (e: 'select', item: string): void
}>()

const selectItem = (item: string) => {
  if (props.disabled) return
  emit('select', item)
}

const cssVars = computed(() => ({
  '--min-height': `${props.height}px`,
  '--max-height': props.autoGrow ? 'none' : `${props.height}px`,
  '--overflowY': props.autoGrow ? 'auto' : 'scroll',
  '--overflowX': 'hidden',
}))

const inputWrapperClass = computed(() => {
  let classStr = 'cr-list__input-wrapper'
  if (props.disabled) classStr += ' cr-list__input-wrapper__disabled'
  return classStr
})

useAttrs()
</script>

<template>
  <div class="cr-list">
    <label
      v-if="label"
      :for="$attrs.id"
    >
      {{ label }}
    </label>
    <div
      :class="inputWrapperClass"
      :style="cssVars"
    >
      <div class="list-container cr-list__input-wrapper__grow">
        <span
          v-for="item in items"
          :key="item || `item-${item}`"
          class="list-item"
          :class="{ selected: item === selectedItem }"
          @click="selectItem(item)"
        >
          {{ item }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cr-list {
  font-family: 'Lato', sans-serif;

  label {
    margin-bottom: 4px;
    display: block;
    font-size: 14px;
  }

  &__input-wrapper {
    background: $input-background-gray;
    border: 1px solid $border-gray;
    padding: 8px 12px;
    border-radius: 5px;
    color: $gray;

    &__grow {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      word-break: break-word;
      min-height: var(--min-height);
      max-height: var(--max-height);
      overflow-y: var(--overflowY);
      overflow-x: var(--overflowX);
    }

    &:hover {
      border-color: $gray-mid-light;
    }

    &__disabled {
      background: $blue-dull !important;
      border-color: $blue-dull;
      color: $gray-light;
      pointer-events: none;
    }
  }

  .list-item {
    cursor: pointer;
    padding: 6px 8px;
    border-radius: 4px;
    margin: 2px 0;
    width: 100%;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: lighten($input-background-gray, 10%);
    }

    &.selected {
      background-color: $blue-dull;
      color: $white;
    }
  }
}
</style>
